import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { useMemo, useCallback } from "react";
import { getIdeaPages } from "../api/idea";
import { useCurrentUser } from "./useCurrentUser";

export const ideaPagesKeys = {
  all: ["ideaPages"] as const,
};

export const useIdeaPages = () => {
  const auth0 = useAuth0();
  const currentUser = useCurrentUser();
  const queryClient = useQueryClient();

  const {
    data: pagesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ideaPagesKeys.all,
    queryFn: async () => {
      const response = await getIdeaPages(auth0);
      return response.ideaPages;
    },
    // Wait for `/auth/me`, not just the Auth0 session: the pages response is
    // scoped to the user, so firing both in parallel can resolve pages against
    // an identity the app has not established yet.
    enabled: !!currentUser,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const pagesMap = useMemo(() => {
    if (!pagesData) return new Map<string, any>();
    return new Map(pagesData.map((page: any) => [page.key, page]));
  }, [pagesData]);

  const getPageByKey = useCallback(
    (key: string) => {
      return pagesMap.get(key) || pagesMap.get("all");
    },
    [pagesMap]
  );

  const invalidatePages = useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: ideaPagesKeys.all });
  }, [queryClient]);

  return {
    pages: pagesData || [],
    pagesMap,
    getPageByKey,
    isLoading,
    error,
    refetch,
    invalidatePages,
  };
};
